import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';

import {
  ClientSupport,
  NamingConvention,
  ProjectSetup,
  SelectedSubproject,
  Subproject,
  UserPreferences,
  UserSession,
} from '@shared/models';
import {
  Activity,
  ActivityGroup,
  ActivityMobilisation,
  ActivityStream,
  ActivityStreamInlineCustomData,
  Approver,
  Consumable,
  CustomData,
  CustomDataValue,
  ForecastCategory,
  HiddenSubtab,
  Hse,
  Location,
  LocationCategory,
  Personnel,
  Progress,
  ProgressWithoutTarget,
  ReportConfiguration,
  StatusTrackerWithData,
  SubprojectConfiguration,
  TableOfColumnWithData,
  Weather,
  WeatherValue,
  FreeTextField,
} from '@shared/models/project-setup';

import { UserDetailsState } from '..';
import { UserDetailsActions } from '../actions';
import { userDetailsQuery } from '../user-details.selector';

@Injectable()
export class UserDetailsFacade {
  query = {
    userDetails$: this.store.select(userDetailsQuery.getUserDetails),
    termsConditionsAccepted$: this.store.select(
      userDetailsQuery.getTermsConditionsAccepted
    ),
    subprojects$: this.store.select(userDetailsQuery.getSubprojects),
    selectedSubprojectId$: this.store.select(
      userDetailsQuery.getSelectedSubprojectId
    ),
    selectedSubproject$: this.store.select(
      userDetailsQuery.getSelectedSubproject
    ),
    subprojectConfiguration$: this.store.select(
      userDetailsQuery.getSubprojectConfiguration
    ),
    colorTypes$: this.store.select(userDetailsQuery.getColorTypes),
    customDataOperators$: this.store.select(
      userDetailsQuery.getCustomDataOperators
    ),
    customTabNames$: this.store.select(userDetailsQuery.getCustomTabNames),
    projectSetup$: this.store.select(userDetailsQuery.getProjectSetup),
    clients$: this.store.select(userDetailsQuery.getClients),
    selectedClientId$: this.store.select(userDetailsQuery.getSelectedClientId),
    selectedClient$: this.store.select(userDetailsQuery.getSelectedClient),
    modules$: this.store.select(userDetailsQuery.getModules),
    accountId$: this.store.select(userDetailsQuery.getAccountId),
    baseAccount$: this.store.select(userDetailsQuery.getBaseAccount),
    accountType$: this.store.select(userDetailsQuery.getAccountType),
    accountRights$: this.store.select(userDetailsQuery.getAccountRights),
  };

  constructor(private store: Store<UserDetailsState>) {}

  setUserSession(userSession: UserSession): void {
    this.store.dispatch(
      UserDetailsActions.setUserSession({ userDetails: userSession })
    );
  }

  updateUserSession(userSession: UserSession): void {
    this.store.dispatch(
      UserDetailsActions.updateUserSession({ userDetails: userSession })
    );
  }

  setUserPreferences(userPreferences: UserPreferences): void {
    this.store.dispatch(
      UserDetailsActions.setUserPreferences({
        userPreferences: userPreferences,
      })
    );
  }

  setTermsConditionsAccepted(termsConditionsAccepted: boolean): void {
    this.store.dispatch(
      UserDetailsActions.setTermsConditionsAccepted({ termsConditionsAccepted })
    );
  }

  setSelectedSubproject(selectedSubproject: SelectedSubproject): void {
    this.store.dispatch(
      UserDetailsActions.setSelectedSubproject({ selectedSubproject })
    );
  }

  setSelectedSubprojectDetails(
    subproject: Subproject,
    projectSetup: ProjectSetup
  ): void {
    if (!subproject) {
      return;
    }
    const { fileUpload, ...subprojectData } = subproject;
    const selectedSubproject: SelectedSubproject = {
      ...subprojectData,
      data: projectSetup,
    };
    this.setSelectedSubproject(selectedSubproject);
  }

  setProjectSetup(projectSetup: ProjectSetup): void {
    this.store.dispatch(UserDetailsActions.setProjectSetup({ projectSetup }));
  }

  setStatusTrackers(statusTrackers: StatusTrackerWithData[]): void {
    this.store.dispatch(
      UserDetailsActions.setStatusTrackers({ statusTrackers })
    );
  }

  setLocationCategories(locationCategoryList: LocationCategory[]): void {
    this.store.dispatch(
      UserDetailsActions.setLocationCategories({ locationCategoryList })
    );
  }

  setLocations(locationList: Location[]): void {
    this.store.dispatch(UserDetailsActions.setLocations({ locationList }));
  }

  setActivityGroup(activityGroupList: ActivityGroup[]): void {
    this.store.dispatch(
      UserDetailsActions.setActivityGroups({ activityGroupList })
    );
  }

  setActivities(activityList: Activity[]): void {
    this.store.dispatch(UserDetailsActions.setActivities({ activityList }));
  }

  setActivityStreams(activityStreams: ActivityStream[]): void {
    this.store.dispatch(
      UserDetailsActions.setActivityStreams({ activityStreams })
    );
  }

  setHse(hseList: Hse[]): void {
    this.store.dispatch(UserDetailsActions.setHse({ hseList }));
  }

  setForecastCategories(forecastCategories: ForecastCategory[]): void {
    this.store.dispatch(
      UserDetailsActions.setForecastCategories({ forecastCategories })
    );
  }

  setConsumables(consumableList: Consumable[]): void {
    this.store.dispatch(UserDetailsActions.setConsumables({ consumableList }));
  }

  setApprovers(approverList: Approver[]): void {
    this.store.dispatch(UserDetailsActions.setApprovers({ approverList }));
  }

  setSelectedClientId(clientId: string): void {
    this.store.dispatch(UserDetailsActions.setSelectedClientId({ clientId }));
  }

  setEnableDigitalSignature(
    updateData: { id: string; enableDigitalSignature: boolean }[]
  ): void {
    this.store.dispatch(
      UserDetailsActions.setEnableDigitalSignature({
        updateData,
      })
    );
  }

  setClientSupport(clientSupport: ClientSupport): void {
    this.store.dispatch(
      UserDetailsActions.setClientSupport({
        clientSupport,
      })
    );
  }

  setSubprojects(subprojects: Subproject[]): void {
    this.store.dispatch(UserDetailsActions.setSubprojects({ subprojects }));
  }

  setSubprojectConfiguration(
    subprojectConfiguration: SubprojectConfiguration
  ): void {
    this.store.dispatch(
      UserDetailsActions.setSubprojectConfiguration({ subprojectConfiguration })
    );
  }

  setCustomData(customDataList: CustomData[]): void {
    this.store.dispatch(UserDetailsActions.setCustomData({ customDataList }));
  }

  setActivityMobilisations(
    activityMobilisationList: ActivityMobilisation[]
  ): void {
    this.store.dispatch(
      UserDetailsActions.setActivityMobilisations({ activityMobilisationList })
    );
  }

  setCustomDataValues(customDataValueList: CustomDataValue[]): void {
    this.store.dispatch(
      UserDetailsActions.setCustomDataValues({ customDataValueList })
    );
  }

  setActivityStreamInlineCustomData(
    activityStreamInlineCustomDataList: ActivityStreamInlineCustomData[]
  ): void {
    this.store.dispatch(
      UserDetailsActions.setActivityStreamInlineCustomData({
        activityStreamInlineCustomDataList,
      })
    );
  }

  setSubprojectReleased(subprojectId: number): void {
    this.store.dispatch(
      UserDetailsActions.setSubprojectReleased({ subprojectId })
    );
  }

  setWeather(weatherList: Weather[]): void {
    this.store.dispatch(UserDetailsActions.setWeather({ weatherList }));
  }

  setWeatherValues(weatherDataTypeValueList: WeatherValue[]): void {
    this.store.dispatch(
      UserDetailsActions.setWeatherValues({ weatherDataTypeValueList })
    );
  }

  setPersonnel(personnelList: Personnel[]): void {
    this.store.dispatch(UserDetailsActions.setPersonnel({ personnelList }));
  }

  setHiddenSubtabs(hiddenSubtabs: HiddenSubtab[]): void {
    this.store.dispatch(UserDetailsActions.setHiddenSubtabs({ hiddenSubtabs }));
  }

  setProgress(progress: Progress): void {
    this.store.dispatch(UserDetailsActions.setProgress({ progress }));
  }

  setProgressWithoutTarget(progressWithoutTarget: ProgressWithoutTarget): void {
    this.store.dispatch(
      UserDetailsActions.setProgressWithoutTarget({ progressWithoutTarget })
    );
  }

  setReportConfiguration(reportConfiguration: ReportConfiguration): void {
    this.store.dispatch(
      UserDetailsActions.setReportConfiguration({
        reportConfiguration,
      })
    );
  }

  setNamingConvention(namingConvention: NamingConvention): void {
    this.store.dispatch(
      UserDetailsActions.setNamingConvention({ namingConvention })
    );
  }

  setTableOfColumns(tableOfColumns: TableOfColumnWithData[]): void {
    this.store.dispatch(
      UserDetailsActions.setTableOfColumns({ tableOfColumns })
    );
  }

  setFreeTextFields(freeTextFields: FreeTextField[]): void {
    this.store.dispatch(
      UserDetailsActions.setFreeTextFields({ freeTextFields })
    );
  }
}
